<template>
  <div>
    <v-row id="suus__post-list">
      <v-col cols="12">
        <div v-if="reverse">
          <PostForm
            id="suus__post-form"
            class="mb-4"
            :inline="true"
            :is-edit="false"
            :createPost="createPost"
            @postCreated="onPostCreated"
            :delete-post="deletePost"
            :viewContext="viewContext"
            :disabled="!canCreatePosts"
            :warning="postFormWarning"
          />
        </div>

        <OmissionLoader
          ref="omissionLoader"
          item-name-plural="Beiträge"
          :load-more="loadPosts"
          :total-count="totalPostsCount"
          :page-size="3"
          :initial-trailing-count="3"
          :initial-leading-count="3"
        >
          <template v-slot:item="props">
            <PostListItem
              :post="props.item"
              :previous-post-created-at="props.item.created_at"
              :update-post="updatePost"
              @postUpdated="onPostUpdated"
              @delete-post="deletePostWithOptimisticUpdate"
              :viewContext="viewContext"
            />
          </template>

          <template
            v-slot:loadIntermediate="{
              loadLeading,
              loadTrailing,
              remainingCount,
              nextPageSize,
            }"
          >
            <div class="d-flex flex-column">
              <TextButton
                @click="loadLeading()"
                :label="`${nextPageSize} frühere Beiträge laden`"
                variant="small"
              />

              <div class="d-flex flex-column">
                <DividerZigZag :backgroundColor="dividerBackgroundColor" />
                <TextBody class="text-center">
                  {{ remainingCount }} zwischenzeitliche Beiträge
                </TextBody>
                <DividerZigZag :backgroundColor="dividerBackgroundColor" />
              </div>

              <TextButton
                @click="loadTrailing()"
                :label="`${nextPageSize} spätere Beiträge laden`"
                variant="small"
              />
            </div>
          </template>

          <template
            v-slot:loadRemaining="{ loadMore, remainingCount, nextPageSize }"
          >
            <div class="d-flex flex-column mb-2">
              <DividerZigZag :background-color="dividerBackgroundColor" />
              <TextButton
                variant="small"
                @click="loadMore()"
                :label="`Restliche ${remainingCount} Beiträge laden`"
              />
              <DividerZigZag :background-color="dividerBackgroundColor" />
            </div>
          </template>

          <template v-slot:loading>
            <PostListSkeleton />
          </template>
        </OmissionLoader>

        <div v-if="!reverse">
          <PostForm
            id="suus__post-form"
            class="mb-4"
            :inline="true"
            :is-edit="false"
            :createPost="createPost"
            @postCreated="onPostCreated"
            :delete-post="deletePost"
            :viewContext="viewContext"
            :disabled="!canCreatePosts"
            :warning="postFormWarning"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { Post } from "suus-api"

import { defineComponent, PropType, Ref, ref, watch } from "vue"

import PostListItem from "./PostListItem.vue"
import PostForm from "features/posts/form/PostForm.vue"
import TextButton from "../../components/UI/TextButton.vue"
import TextBody from "../../components/UI/TextBody.vue"
import PostListSkeleton from "./PostListSkeleton.vue"
import { ViewContext } from "concerns/useContextName"
import OmissionLoader from "features/omission-loader/OmissionLoader.vue"
import { LoadMoreFunction } from "features/omission-loader/use-omission-loader-internal"
import DividerZigZag from "components/UI/DividerZigZag.vue"
import { useThemeColors } from "features/common/theme/useTheme"

// returns Post if successful, null if not
export type CreatePostOfEntityFunction = (
  entityId: number,
  postText: string
) => Promise<Post | null>

// returns Post if successful, null if not
export type UpdatePostOfEntityFunction = (
  entityId: number,
  postId: number,
  postText: string
) => Promise<Post | null>

export type DeletePostOfEntityFunction = (
  entityId: number,
  postId: number
) => Promise<boolean>

export default defineComponent({
  name: "PostList",
  props: {
    totalPostsCount: { type: Number, required: true },
    entityId: { type: Number, required: true },
    canCreatePosts: { type: Boolean, default: false },
    postFormWarning: { type: String, default: null },
    viewContext: { type: Object as PropType<ViewContext> },
    loadPosts: {
      type: Function as PropType<LoadMoreFunction>,
      required: true,
    },
    performCreatePost: {
      type: Function as PropType<CreatePostOfEntityFunction>,
      required: true,
    },
    performUpdatePost: {
      type: Function as PropType<UpdatePostOfEntityFunction>,
      required: true,
    },
    performDeletePost: {
      type: Function as PropType<DeletePostOfEntityFunction>,
      required: true,
    },
    reverse: { type: Boolean, default: false },
  },
  components: {
    DividerZigZag,
    OmissionLoader,
    PostListItem,
    TextButton,
    TextBody,
    PostForm,
    PostListSkeleton,
  },

  setup(props, context) {
    const omissionLoader = ref<InstanceType<typeof OmissionLoader>>(null)

    const totalCount: Ref<number> = ref(props.totalPostsCount)

    const dividerBackgroundColor = useThemeColors().surface

    watch(
      () => props.totalPostsCount,
      (newTotalCount) => {
        totalCount.value = newTotalCount
      }
    )

    const increaseTotalCount = () => {
      totalCount.value += 1
      context.emit("optimistic-posts-count", totalCount.value)
    }

    const decreaseTotalCount = () => {
      totalCount.value -= 1
      context.emit("optimistic-posts-count", totalCount.value)
    }

    async function updatePost(postId: number, postText: string) {
      const updatedPost = await props.performUpdatePost(
        props.entityId,
        postId,
        postText
      )
      if (!updatedPost)
        throw new Error("Der Beitrag konnte nicht aktualisiert werden.")
      return updatedPost
    }

    async function onPostUpdated(updatedPost: Post) {
      const updateFunction = (oldPost: Post) => {
        oldPost.text = updatedPost.text
        oldPost.attachments = updatedPost.attachments
        oldPost.event_media_count = updatedPost.event_media_count
        oldPost.event_media_preview = updatedPost.event_media_preview
        return oldPost
      }
      omissionLoader.value?.updateItem(updatedPost.id, updateFunction)
    }

    async function deletePostWithOptimisticUpdate(
      postId: number
    ): Promise<void> {
      const isSuccess = await props.performDeletePost(props.entityId, postId)
      if (isSuccess) {
        decreaseTotalCount()
        omissionLoader.value?.removeItem(postId)
      }
    }

    const onPostCreated = (createdPost: Post) => {
      increaseTotalCount()
      if (props.reverse) omissionLoader.value?.addItemToLeading(createdPost)
      else omissionLoader.value?.addItemToTrailing(createdPost)
    }

    const createPost = async (postText: string): Promise<Post> => {
      const createdPost = await props.performCreatePost(
        props.entityId,
        postText
      )
      if (!createdPost)
        throw new Error(
          "Bei der Erstellung des Beitrags ist ein Fehler aufgetreten."
        )
      return createdPost
    }

    const deletePost = async (postId: number): Promise<boolean> => {
      return await props.performDeletePost(props.entityId, postId)
    }

    return {
      createPost,
      deletePost,
      onPostCreated,
      updatePost,
      onPostUpdated,
      deletePostWithOptimisticUpdate,
      totalCount,
      dividerBackgroundColor,
      omissionLoader,
    }
  },
})
</script>
